<template>
    <v-dialog v-model="dialog" max-width="1000px" persistent>
        <v-card :loading="loading">
            <v-card-title class="text-h5">
                <v-btn
                    class="mr-3"
                    depressed
                    color="primary"
                    icon
                    rounded
                    @click="closeDialog"
                >
                    <v-icon>mdi-close</v-icon>
                </v-btn>
                Change Orders
                <v-spacer></v-spacer>
            </v-card-title>

            <v-card-text>
                <v-data-table
                    :headers="headers"
                    :items="changeOrders"
                    :loading="loading"
                    item-key="id"
                    class="elevation-1"
                    show-expand
                >
                    <template v-slot:item.requestDate="{ item }">
                        {{ formatDate(item.requestDate) }}
                    </template>
                    <template v-slot:item.dueDate="{ item }">
                        {{ formatDate(item.dueDate) }}
                    </template>
                    <template v-slot:item.requestedBy="{ item }">
                        {{ getUserName(item.requestedBy) }}
                    </template>
                    <template v-slot:item.createdOn="{ item }">
                        {{ formatDate(item.createdOn) }}
                    </template>
                    <template v-slot:item.actions="{ item }">
                        <v-btn icon small @click.stop="editChangeOrder(item)">
                            <v-icon>mdi-pencil</v-icon>
                        </v-btn>
                    </template>

                    <!-- Expand template for logs -->
                    <template v-slot:expanded-item="{ headers, item }">
                        <td :colspan="headers.length" class="pa-4">
                            <div class="d-flex align-center mb-3">
                                <v-icon class="mr-2" color="primary">
                                    mdi-history
                                </v-icon>
                                <h4 class="text-h6 mb-0">Change History</h4>
                                <v-spacer></v-spacer>
                                <v-chip small color="primary" outlined>
                                    {{ item.logs ? item.logs.length : 0 }}
                                    entries
                                </v-chip>
                            </div>

                            <!-- Logs Timeline -->
                            <div v-if="item.logs && item.logs.length > 0">
                                <v-timeline dense>
                                    <v-timeline-item
                                        v-for="(log, index) in item.logs"
                                        :key="index"
                                        :color="getLogColor(log)"
                                        small
                                    >
                                        <template v-slot:icon>
                                            <v-icon small>{{
                                                getLogIcon(log)
                                            }}</v-icon>
                                        </template>

                                        <div class="d-flex align-center mb-2">
                                            <v-chip
                                                :color="getLogColor(log)"
                                                small
                                                class="mr-2"
                                            >
                                                {{ getLogActionText(log) }}
                                            </v-chip>
                                            <span class="font-weight-medium">
                                                {{
                                                    getUserName(log.modifiedBy)
                                                }}
                                            </span>
                                            <v-spacer></v-spacer>
                                            <span
                                                class="text-caption text--secondary"
                                            >
                                                {{ formatDate(log.modifiedOn) }}
                                            </span>
                                        </div>

                                        <!-- Changes Summary -->
                                        <div
                                            v-if="
                                                log.changes &&
                                                    Object.keys(log.changes)
                                                        .length > 0
                                            "
                                            class="mb-2"
                                        >
                                            <div class="text-body-2 mb-2">
                                                <strong>Updated Fields:</strong>
                                            </div>

                                            <!-- Detailed Changes -->
                                            <v-expansion-panels flat>
                                                <v-expansion-panel>
                                                    <v-expansion-panel-header>
                                                        <span
                                                            class="text-caption"
                                                        >
                                                            View
                                                            {{
                                                                Object.keys(
                                                                    log.changes
                                                                ).length
                                                            }}
                                                            updated fields
                                                        </span>
                                                    </v-expansion-panel-header>
                                                    <v-expansion-panel-content>
                                                        <div
                                                            v-for="(change,
                                                            field) in log.changes"
                                                            :key="field"
                                                            class="mb-3"
                                                        >
                                                            <div
                                                                class="font-weight-medium text-caption mb-1"
                                                            >
                                                                {{
                                                                    getFieldDisplayName(
                                                                        field
                                                                    )
                                                                }}
                                                            </div>
                                                            <div
                                                                v-if="
                                                                    field ===
                                                                        'requestDescription'
                                                                "
                                                                class="mt-2"
                                                            >
                                                                <v-chip
                                                                    small
                                                                    color="blue lighten-4"
                                                                    text-color="blue darken-2"
                                                                    class="mb-2"
                                                                >
                                                                    Updated
                                                                </v-chip>
                                                                <div
                                                                    class="text-body-2"
                                                                >
                                                                    <vue-editor
                                                                        :editor-options="
                                                                            editorOptions
                                                                        "
                                                                        :value="
                                                                            change
                                                                        "
                                                                    />
                                                                </div>
                                                            </div>
                                                            <div
                                                                v-else
                                                                class="d-flex align-center"
                                                            >
                                                                <v-chip
                                                                    small
                                                                    color="blue lighten-4"
                                                                    text-color="blue darken-2"
                                                                    class="mr-2"
                                                                >
                                                                    Updated
                                                                </v-chip>
                                                                <div
                                                                    class="text-body-2"
                                                                >
                                                                    {{
                                                                        formatLogValue(
                                                                            field,
                                                                            change
                                                                        )
                                                                    }}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </v-expansion-panel-content>
                                                </v-expansion-panel>
                                            </v-expansion-panels>
                                        </div>

                                        <!-- No changes message -->
                                        <div
                                            v-else
                                            class="text-caption text--secondary"
                                        >
                                            No specific field updates recorded
                                        </div>
                                    </v-timeline-item>
                                </v-timeline>
                            </div>

                            <!-- No logs message -->
                            <div v-else class="text-center py-4">
                                <v-icon color="grey" size="48">
                                    mdi-history
                                </v-icon>
                                <div class="text-h6 mt-2">
                                    No Change History
                                </div>
                                <div class="text-caption text--secondary">
                                    No changes have been recorded for this
                                    change order.
                                </div>
                            </div>
                        </td>
                    </template>
                </v-data-table>
            </v-card-text>

            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn text color="primary" @click="closeDialog">
                    Close
                </v-btn>
            </v-card-actions>
        </v-card>

        <!-- Change Order Form Dialog -->
        <ChangeOrderFormDialog
            v-model="formDialog"
            :existing-data="selectedChangeOrder"
            :is-editing="isEditing"
            :quote-status="quoteStatus"
            :lead-status="leadStatus"
            :is-archived="isArchived"
            :change-order-id="selectedChangeOrderId"
            @save="handleChangeOrderSave"
            @cancel="handleChangeOrderCancel"
        />
    </v-dialog>
</template>

<script>
import { mapMutations } from 'vuex'
import moment from 'moment'
import ChangeOrderFormDialog from './ChangeOrderFormDialog.vue'
import { VueEditor } from 'vue2-editor'

export default {
    name: 'ChangeOrderListDialog',
    components: {
        ChangeOrderFormDialog,
        VueEditor,
    },
    props: {
        value: {
            type: Boolean,
            default: false,
        },
        changeOrders: {
            type: Array,
            default: () => [],
        },
        users: {
            type: Array,
            default: () => [],
        },
        quoteStatus: {
            type: String,
            default: undefined,
        },
        leadStatus: {
            type: String,
            default: undefined,
        },
        isArchived: {
            type: Boolean,
            default: false,
        },
        updatedChangeOrders: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            dialog: false,
            loading: false,
            formDialog: false,
            isEditing: false,
            selectedChangeOrder: null,
            selectedChangeOrderId: null,
            headers: [
                { text: 'CO#', value: 'coNumber', sortable: true },
                { text: 'Request Date', value: 'requestDate', sortable: true },
                { text: 'Due Date', value: 'dueDate', sortable: true },
                { text: 'Requested By', value: 'requestedBy', sortable: true },
                { text: 'Created On', value: 'createdOn', sortable: true },
                { text: 'Actions', value: 'actions', sortable: false },
            ],
        }
    },
    computed: {
        canEdit() {
            // Use quoteStatus if available (for quotes), otherwise use leadStatus (for leads)
            const status =
                this.quoteStatus !== undefined
                    ? this.quoteStatus
                    : this.leadStatus

            // For quotes: can edit if status is OPEN or APPROVAL and not archived
            if (this.quoteStatus !== undefined) {
                return (
                    (status === 'OPEN' || status === 'APPROVAL') &&
                    !this.isArchived
                )
            }

            // For leads: can edit if inProgress, onHold, or undefined
            return (
                status === 'inProgress' ||
                status === 'onHold' ||
                status === undefined
            )
        },
        editorOptions() {
            return {
                theme: 'snow',
                readOnly: true,
                modules: {
                    toolbar: false,
                },
            }
        },
    },
    watch: {
        value(newVal) {
            this.dialog = newVal
        },
        dialog(newVal) {
            this.$emit('input', newVal)
        },
        updatedChangeOrders() {
            if (this.updatedChangeOrders.length > 0) {
                this.changeOrders = this.updatedChangeOrders
            }
        },
    },
    methods: {
        ...mapMutations(['setErrorItems']),

        closeDialog() {
            this.dialog = false
            this.formDialog = false
            this.selectedChangeOrder = null
            this.selectedChangeOrderId = null
            this.isEditing = false
        },

        editChangeOrder(item) {
            this.selectedChangeOrder = item
            this.selectedChangeOrderId = item.id
            this.isEditing = true
            this.formDialog = true
        },

        handleChangeOrderSave(requestData) {
            // Emit the saved data to parent component
            this.$emit('save', {
                ...requestData,
                id: this.selectedChangeOrderId,
            })
            this.formDialog = false
        },

        handleChangeOrderCancel() {
            this.formDialog = false
        },

        formatDate(date) {
            if (!date) return ''
            if (typeof date === 'string') {
                return moment(date).format('MMM DD, YYYY')
            }
            if (date._seconds) {
                return moment.unix(date._seconds).format('MMM DD, YYYY')
            }
            return moment(date).format('MMM DD, YYYY')
        },

        getUserName(userId) {
            const user = this.users.find(u => u.id === userId)
            return user ? user.name : 'Unknown'
        },

        // Log-related methods
        getLogColor(log) {
            // Determine color based on log type or changes
            if (log.changes && Object.keys(log.changes).length > 0) {
                return 'blue'
            }
            return 'grey'
        },

        getLogIcon(log) {
            // Determine icon based on log type
            if (log.changes && Object.keys(log.changes).length > 0) {
                return 'mdi-pencil'
            }
            return 'mdi-information'
        },

        getLogActionText(log) {
            // Determine action text based on log content
            if (log.changes && Object.keys(log.changes).length > 0) {
                const changeCount = Object.keys(log.changes).length
                return `${changeCount} field${
                    changeCount > 1 ? 's' : ''
                } updated`
            }
            return 'Updated'
        },

        getFieldDisplayName(field) {
            const fieldNames = {
                requestDate: 'Request Date',
                requiresPredesign: 'Requires Predesign',
                dueDate: 'Due Date',
                requestedBy: 'Requested By',
                targetPrice: 'Target Price',
                projectId: 'Project ID',
                drawingsSetDate: 'Drawings Set Date',
                coNumber: 'CO Number',
                sendToPmClient: 'Send to PM Client',
                finishNameAndUc: 'Finish Name and UC',
                keyDrawings: 'Key Drawings',
                requestDescription: 'Request Description',
                subject: 'Subject',
                status: 'Status',
                priority: 'Priority',
                notes: 'Notes',
                attachments: 'Attachments',
                approvalStatus: 'Approval Status',
                approvedBy: 'Approved By',
                approvedDate: 'Approved Date',
            }
            return fieldNames[field] || field
        },

        formatLogValue(field, value) {
            if (value === null || value === undefined) {
                return '-'
            }

            if (value instanceof Date) {
                return value.toLocaleDateString()
            }

            if (Array.isArray(value)) {
                return value.length > 0 ? value.join(', ') : 'Empty array'
            }

            if (typeof value === 'object') {
                return JSON.stringify(value, null, 2)
            }

            if (typeof value === 'boolean') {
                return value ? 'Yes' : 'No'
            }

            if (typeof value === 'string' && field != 'requestDescription') {
                const userName = this.getUserName(value)
                return userName == 'Unknown' ? value : userName
            }

            // requestDescription is now handled in the template with vue-editor
            return String(value)
        },
    },
}
</script>

<style scoped>
.v-data-table >>> tbody tr {
    cursor: pointer;
}
</style>
